// Dono: B3. Faixa "Em foco" abaixo da busca (até 8, arrastar para ordenar) e botão de fixar usado no detalhe.
// Arrastar um card da grade para a faixa fixa; arrastar um da faixa para fora solta. Teclado: Shift+←/→ move, Delete solta.
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { type Card, LIMITS } from "../../../../shared/types.ts";
import "../../styles/organize.css";
import { IconClose } from "../Icons.tsx";
import { cardText, KIND_LABEL } from "../lib.ts";
import { byId, failed, store, toast } from "../state.ts";
import { Thumb } from "../spaces/common.tsx";
import { IconPin } from "../spaces/icons.tsx";
import { dragging, droppedIds, endCardDrag, isCardDrag, startCardDrag } from "../spaces/selection.ts";

const PIN_MIME = "application/x-faig-pin";
const FULL = `Em foco cabem ${LIMITS.pins} cards. Tire um para fixar outro.`; // o mesmo aviso do store
let pinDrag: string | null = null; // card da faixa sendo arrastado (soltar fora da faixa = tirar do foco)

const nameOf = (c: Card) => `${KIND_LABEL[c.kind]}: ${cardText(c).slice(0, 60)}`;
const focusPin = (id: string) => requestAnimationFrame(() => document.querySelector<HTMLElement>(`.pin a[href="#${CSS.escape(encodeURIComponent(id))}"]`)?.focus());

async function pinAll(ids: string[]) {
  for (const id of ids) if (!(await store.pin(id))) break; // o 9º é recusado pelo store, com aviso
}
/** Tira do foco; Desfazer devolve o card ao mesmo lugar da faixa (D27), não ao fim. */
function unpin(c: Card) {
  const at = store.pins.peek().find((p) => p.cardId === c.id)?.position ?? undefined;
  store.unpin(c.id).then(() => toast({ key: `unpin:${c.id}`, text: "Saiu do foco.", action: { label: "Desfazer", run: () => void store.pin(c.id, at).catch(failed) } }), failed);
}

export function PinsStrip() {
  const id = useId();
  const strip = useRef<HTMLElement>(null);
  const [over, setOver] = useState<{ to: number } | null>(null);
  const [said, setSaid] = useState("");
  const map = byId.value;
  const pins = store.pins.value.map((p) => map.get(p.cardId)).filter((c): c is Card => !!c && !c.isPrivate);
  const ids = pins.map((c) => c.id);
  const full = store.pins.value.length >= LIMITS.pins;

  // Card da faixa solto fora dela: sai do foco (cancelar com Esc não solta nada).
  useEffect(() => {
    const outside = (e: DragEvent) => !!pinDrag && !strip.current?.contains(e.target as Node);
    const onOver = (e: DragEvent) => outside(e) && e.preventDefault();
    const onDrop = (e: DragEvent) => {
      if (!outside(e) || e.defaultPrevented) return;
      e.preventDefault();
      const c = map.get(pinDrag!);
      if (c) unpin(c);
    };
    document.addEventListener("dragover", onOver);
    document.addEventListener("drop", onDrop);
    return () => (document.removeEventListener("dragover", onOver), document.removeEventListener("drop", onDrop));
  });

  const move = (cardId: string, to: number) => {
    const from = ids.indexOf(cardId);
    if (from < 0 || to < 0 || to >= ids.length || to === from) return;
    const next = ids.filter((x) => x !== cardId);
    next.splice(to, 0, cardId);
    store.reorderPins(next).catch(failed);
    setSaid(`Posição ${to + 1} de ${ids.length}.`);
    focusPin(cardId);
  };
  const dropAt = (e: DragEvent) => {
    const li = (e.target as Element).closest<HTMLElement>(".pin");
    if (!li) return ids.length;
    const r = li.getBoundingClientRect();
    return Number(li.dataset.i) + (e.clientX > r.left + r.width / 2 ? 1 : 0);
  };

  if (!pins.length && !dragging.value) return null;
  return (
    <section
      class="pins"
      ref={strip}
      aria-labelledby={`${id}-t`}
      data-over={over ? "" : undefined}
      onDragOver={(e) => {
        if (!isCardDrag(e)) return;
        e.preventDefault();
        e.dataTransfer!.dropEffect = "move";
        setOver({ to: dropAt(e) });
      }}
      onDragLeave={(e) => !strip.current?.contains(e.relatedTarget as Node) && setOver(null)}
      onDrop={(e) => {
        if (!isCardDrag(e)) return;
        e.preventDefault();
        const to = dropAt(e);
        setOver(null);
        const moving = e.dataTransfer?.getData(PIN_MIME);
        endCardDrag();
        if (moving) return move(moving, to > ids.indexOf(moving) ? to - 1 : to);
        if (full) return toast({ key: "pins-full", text: FULL });
        void pinAll(droppedIds(e).filter((x) => !ids.includes(x))).catch(failed);
      }}
    >
      <div class="pins-head">
        <h2 id={`${id}-t`} class="pins-title">
          Em foco
        </h2>
        <span class="pins-count">
          {pins.length} de {LIMITS.pins}
        </span>
        {full && <span class="pins-full">Cheio: tire um card para pôr outro.</span>}
      </div>
      {pins.length ? (
        <ol class="pins-row" aria-describedby={`${id}-h`}>
          {pins.map((c, i) => (
            <li
              key={c.id}
              class="pin"
              data-i={i}
              data-drop={over && (over.to === i ? "before" : over.to === i + 1 && i === pins.length - 1 ? "after" : undefined)}
              draggable
              onDragStart={(e) => {
                startCardDrag(e, [c.id]);
                e.dataTransfer?.setData(PIN_MIME, c.id);
                pinDrag = c.id;
              }}
              onDragEnd={() => {
                pinDrag = null;
                endCardDrag();
                setOver(null);
              }}
            >
              <a
                class="pin-open"
                href={`#${encodeURIComponent(c.id)}`}
                aria-label={nameOf(c)}
                draggable={false}
                onKeyDown={(e) => {
                  if (e.shiftKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
                    e.preventDefault();
                    move(c.id, i + (e.key === "ArrowRight" ? 1 : -1));
                  } else if (e.key === "Delete" || e.key === "Backspace") {
                    e.preventDefault();
                    unpin(c);
                    requestAnimationFrame(() => document.querySelector<HTMLElement>(".pin a, #main")?.focus());
                  }
                }}
              >
                <Thumb card={c} />
              </a>
              <button type="button" class="pin-x" aria-label={`Tirar do foco: ${nameOf(c)}`} onClick={() => unpin(c)}>
                <IconClose />
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p class="pins-drop">
          <IconPin />
          Solte aqui para pôr em foco
        </p>
      )}
      <p id={`${id}-h`} class="sr-only">
        Shift + seta move o card na faixa; Delete tira do foco.
      </p>
      <p class="sr-only" aria-live="polite">
        {said}
      </p>
    </section>
  );
}

/** "Pôr em foco" no detalhe: liga e desliga; o 9º é recusado com o aviso do store. */
export function PinButton({ card }: { card: Card }) {
  const on = store.pins.value.some((p) => p.cardId === card.id);
  const label = on ? "Tirar do foco" : "Pôr em foco";
  return (
    <button
      type="button"
      class="round pin-btn"
      aria-pressed={on}
      aria-label="Em foco"
      data-tip={label}
      onClick={() =>
        (on ? store.unpin(card.id).then(() => true) : store.pin(card.id)).then((ok) => ok && toast({ key: `pin:${card.id}:${on}`, text: on ? "Saiu do foco." : "Em foco: fica no topo de Tudo." }), failed)
      }
    >
      <IconPin />
    </button>
  );
}

